import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, ReactNode } from 'react';
import { useCOAProgress, COAStatusResult } from '@/hooks/useCOAProgress';
import { supabase } from '@/integrations/supabase/client';
import { countCOASuggestions } from '@/lib/coaSuggestions';
import { trackActivity } from '@/lib/activityTracking';
import { toast } from 'sonner';

interface COAJob {
  jobId: string;
  listingId: string;
  startedAt: number;
}

export type StrainCheckReason = 'mismatch' | 'unreadable' | 'not_found' | 'low_confidence';

export interface PendingCOAConfirm {
  listingId: string;
  reason: StrainCheckReason;
  detectedStrain?: string;
  listingStrain?: string;
  documentPath: string;
  coaUrl: string;
  companyId: string;
  filename: string;
  createdAt: number;
}

interface StartProcessingArgs {
  listingId: string;
  documentPath: string;
  companyId: string;
  listingStrain?: string | null;
  force?: boolean;
  /** Called when sync success/not_a_coa returns — used by ListingDocuments to refresh form. */
  onSyncSuccess?: () => void;
}

interface COAProcessingContextValue {
  startJob: (jobId: string, listingId: string) => void;
  /** Resolves with what happened, so callers (e.g. the chat draft card) can show truthful progress. */
  startProcessing: (args: StartProcessingArgs) => Promise<COAOutcome>;
  getJobForListing: (listingId: string) => COAJob | null;
  getResultForListing: (listingId: string) => COAStatusResult | null;
  isProcessing: (listingId: string) => boolean;
  dismissJob: (listingId: string) => void;
  getStatusMessage: (listingId: string) => string | null;
  // Banner state — shows when a job completes and user is not on listing edit page
  bannerJob: { listingId: string; result: COAStatusResult } | null;
  dismissBanner: () => void;
  bannerSuggestionCount: number;
  // Strain-mismatch confirmation — survives navigation
  pendingConfirm: PendingCOAConfirm | null;
  confirmForce: (listingId?: string) => Promise<COAOutcome>;
  declineForce: (listingId?: string) => void;
  getPendingConfirm: (listingId: string) => PendingCOAConfirm | null;
}

/** queued = background job running; applied/saved = finished now; review = strain check needs the user; failed = not analyzed. */
export type COAOutcome = 'queued' | 'applied' | 'saved' | 'review' | 'failed';

const COAProcessingContext = createContext<COAProcessingContextValue | null>(null);

const POLLING_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
const PENDING_CONFIRM_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const LS_KEY = 'coa_processing_jobs';
const LS_PENDING_KEY = 'coa_pending_confirms';

function loadPersistedJobs(): COAJob[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function persistJobs(jobs: COAJob[]) {
  try {
    if (jobs.length === 0) {
      localStorage.removeItem(LS_KEY);
    } else {
      localStorage.setItem(LS_KEY, JSON.stringify(jobs));
    }
  } catch {}
}

function loadPersistedConfirms(): PendingCOAConfirm[] {
  try {
    const raw = localStorage.getItem(LS_PENDING_KEY);
    const parsed = raw ? (JSON.parse(raw) as PendingCOAConfirm[]) : [];
    const now = Date.now();
    return parsed.filter(p => now - p.createdAt < PENDING_CONFIRM_TTL_MS);
  } catch { return []; }
}

function persistConfirms(confirms: PendingCOAConfirm[]) {
  try {
    if (confirms.length === 0) {
      localStorage.removeItem(LS_PENDING_KEY);
    } else {
      localStorage.setItem(LS_PENDING_KEY, JSON.stringify(confirms));
    }
  } catch {}
}

/**
 * Inner component that polls for a single COA job and reports results up.
 */
function COAJobPoller({
  job,
  onComplete,
  onTimeout,
  onStatusUpdate,
}: {
  job: COAJob;
  onComplete: (listingId: string, result: COAStatusResult) => void;
  onTimeout: (listingId: string) => void;
  onStatusUpdate: (listingId: string, message: string) => void;
}) {
  const { result, isComplete } = useCOAProgress(job.jobId);
  const reportedRef = useRef(false);
  // Tracks the last status message we forwarded so we only suppress
  // exact duplicates. Previously `reportedRef` was reused here, which
  // silently dropped every status update after the first one — the pill
  // looked stuck even while FastAPI was advancing through phases.
  const lastMessageRef = useRef<string | null>(null);

  // Timeout check
  useEffect(() => {
    if (Date.now() - job.startedAt > POLLING_TIMEOUT_MS) {
      onTimeout(job.listingId);
    }
  }, [job, onTimeout]);

  // Report intermediate status messages on every poll
  useEffect(() => {
    if (!result?.message) return;
    if (lastMessageRef.current === result.message) return;
    lastMessageRef.current = result.message;
    onStatusUpdate(job.listingId, result.message);
  }, [result?.message, job.listingId, onStatusUpdate]);

  useEffect(() => {
    if (isComplete && result && !reportedRef.current) {
      reportedRef.current = true;
      onComplete(job.listingId, result);
    }
  }, [isComplete, result, job.listingId, onComplete]);

  return null;
}

export function COAProcessingProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<COAJob[]>(() => {
    // Filter out timed-out jobs on load
    const loaded = loadPersistedJobs();
    return loaded.filter(j => Date.now() - j.startedAt < POLLING_TIMEOUT_MS);
  });
  const [completedResults, setCompletedResults] = useState<Map<string, COAStatusResult>>(new Map());
  const [statusMessages, setStatusMessages] = useState<Map<string, string>>(new Map());
  const [bannerJob, setBannerJob] = useState<{ listingId: string; result: COAStatusResult } | null>(null);
  const [bannerSuggestionCount, setBannerSuggestionCount] = useState(0);
  const [pendingConfirms, setPendingConfirms] = useState<PendingCOAConfirm[]>(() => loadPersistedConfirms());

  // Persist jobs when they change
  useEffect(() => {
    persistJobs(jobs);
  }, [jobs]);

  // Persist pending confirms
  useEffect(() => {
    persistConfirms(pendingConfirms);
  }, [pendingConfirms]);

  const startJob = useCallback((jobId: string, listingId: string) => {
    setJobs(prev => {
      // Replace existing job for same listing
      const filtered = prev.filter(j => j.listingId !== listingId);
      return [...filtered, { jobId, listingId, startedAt: Date.now() }];
    });
    // Clear any previous result for this listing
    setCompletedResults(prev => {
      const next = new Map(prev);
      next.delete(listingId);
      return next;
    });
    // Set initial status message
    setStatusMessages(prev => new Map(prev).set(listingId, 'Queued for analysis...'));
  }, []);

  const handleStatusUpdate = useCallback((listingId: string, message: string) => {
    setStatusMessages(prev => {
      const current = prev.get(listingId);
      if (current === message) return prev; // avoid unnecessary re-renders
      return new Map(prev).set(listingId, message);
    });
  }, []);

  const handleComplete = useCallback((listingId: string, result: COAStatusResult) => {
    setCompletedResults(prev => new Map(prev).set(listingId, result));
    setJobs(prev => prev.filter(j => j.listingId !== listingId));
    // Clean up status message
    setStatusMessages(prev => {
      const next = new Map(prev);
      next.delete(listingId);
      return next;
    });
    // Show banner for completed/failed jobs
    if (result.status === 'completed' || result.status === 'failed') {
      setBannerJob({ listingId, result });
      setBannerSuggestionCount(0);
      if (result.status === 'completed') {
        // Best-effort: count pending suggestions so the banner copy can say
        // "N suggestions to review". Failure here is non-fatal.
        (async () => {
          const { data, error } = await supabase
            .from('listings')
            .select('metadata')
            .eq('id', listingId)
            .maybeSingle();
          if (error || !data) return;
          const meta = (data.metadata ?? {}) as Record<string, unknown>;
          const inferred = (meta?.coa_inferred ?? null) as Record<string, unknown> | null;
          setBannerSuggestionCount(countCOASuggestions(inferred));
        })();
      }
    }
  }, []);

  const handleTimeout = useCallback((listingId: string) => {
    setJobs(prev => prev.filter(j => j.listingId !== listingId));
    setStatusMessages(prev => {
      const next = new Map(prev);
      next.delete(listingId);
      return next;
    });
    const timeoutResult: COAStatusResult = {
      job_id: '',
      status: 'failed',
      message: 'COA processing timed out. Please try again.',
      coa_fields_extracted: null,
      coa_strain: null,
      listing_strain: null,
      extraction_cost_usd: null,
      updated_listing: null,
    };
    setCompletedResults(prev => new Map(prev).set(listingId, timeoutResult));
    setBannerJob({ listingId, result: timeoutResult });
  }, []);

  const getJobForListing = useCallback((listingId: string) => {
    return jobs.find(j => j.listingId === listingId) || null;
  }, [jobs]);

  const getResultForListing = useCallback((listingId: string) => {
    return completedResults.get(listingId) || null;
  }, [completedResults]);

  const isProcessing = useCallback((listingId: string) => {
    return jobs.some(j => j.listingId === listingId);
  }, [jobs]);

  const getStatusMessage = useCallback((listingId: string) => {
    return statusMessages.get(listingId) || null;
  }, [statusMessages]);

  const dismissJob = useCallback((listingId: string) => {
    setCompletedResults(prev => {
      const next = new Map(prev);
      next.delete(listingId);
      return next;
    });
    setStatusMessages(prev => {
      const next = new Map(prev);
      next.delete(listingId);
      return next;
    });
    setJobs(prev => prev.filter(j => j.listingId !== listingId));
    // Note: bannerJob is intentionally NOT cleared here.
    // Use dismissBanner() to clear the banner explicitly.
  }, []);

  const dismissBanner = useCallback(() => {
    setBannerJob(null);
    setBannerSuggestionCount(0);
  }, []);

  // ---- Strain-mismatch confirmation -------------------------------------

  const pendingConfirm = pendingConfirms[0] ?? null;

  // Internal: invoke process-coa with proper sync/async routing.
  const invokeProcessCOA = useCallback(async (
    args: StartProcessingArgs & { signedUrl: string; filename: string },
    force: boolean,
  ): Promise<COAOutcome> => {
    const { listingId, documentPath, companyId, listingStrain, signedUrl, filename, onSyncSuccess } = args;
    try {
      const { data, error } = await supabase.functions.invoke('process-coa', {
        body: {
          coa_url: signedUrl,
          listing_id: listingId,
          company_id: companyId,
          force,
        },
      });
      if (error) {
        console.error('COA processing error:', error);
        toast.error('Processing error', {
          description: 'Something went wrong. Document was saved but not analyzed.',
        });
        return 'failed';
      }
      const result = data as {
        job_id?: string;
        status?: string;
        coa_fields_extracted?: string[];
        coa_strain?: string | null;
        detected_strain?: string | null;
        reason?: StrainCheckReason;
      };

      // Async path
      if (result.job_id) {
        startJob(result.job_id, listingId);
        toast.success(force ? 'Force-processing COA...' : 'Analyzing COA...', {
          description: force
            ? 'Lab data will be applied; the listing strain name will not change.'
            : 'Processing in the background. You can continue working.',
        });
        return 'queued';
      }

      // Sync paths
      if (result.status === 'success' || result.status === 'success_forced') {
        const fieldsText = result.coa_fields_extracted?.length
          ? result.coa_fields_extracted.join(', ')
          : 'lab data';
        toast.success(result.status === 'success_forced' ? 'Lab data applied' : 'COA processed!', {
          description: result.status === 'success_forced'
            ? `Extracted ${fieldsText}. Review suggested field updates on the listing.`
            : `Extracted ${fieldsText}.`,
        });
        onSyncSuccess?.();
        return 'applied';
      } else if (result.status === 'not_a_coa') {
        toast.success('Document saved', { description: 'File added to listing documents.' });
        return 'saved';
      } else if (result.status === 'strain_mismatch' || result.status === 'strain_check_failed') {
        const reason: StrainCheckReason =
          result.status === 'strain_mismatch'
            ? 'mismatch'
            : (result.reason ?? 'mismatch');
        const confirm: PendingCOAConfirm = {
          listingId,
          reason,
          detectedStrain: result.detected_strain || result.coa_strain || undefined,
          listingStrain: listingStrain ?? undefined,
          documentPath,
          coaUrl: signedUrl,
          companyId,
          filename,
          createdAt: Date.now(),
        };
        setPendingConfirms(prev => {
          // Replace any existing confirm for the same listing.
          const filtered = prev.filter(p => p.listingId !== listingId);
          return [...filtered, confirm];
        });
        trackActivity('coa_strain_check_failed', { listing_id: listingId, reason });
        return 'review';
      } else if (result.status === 'extraction_failed') {
        toast.error('Document saved', {
          description: "COA data couldn't be extracted. The file may be unreadable.",
        });
        return 'failed';
      }
      return 'saved';
    } catch (err) {
      console.error('COA processing error:', err);
      toast.error('Processing error', {
        description: 'Something went wrong. Document was saved but not analyzed.',
      });
      return 'failed';
    }
  }, [startJob]);

  const startProcessing = useCallback(async (args: StartProcessingArgs): Promise<COAOutcome> => {
    const { documentPath, force = false } = args;
    if (!args.companyId) {
      console.warn('No companyId provided, skipping COA processing');
      return 'failed';
    }
    // Fresh signed URL (private bucket).
    const { data: signedUrlData, error: signedUrlError } = await supabase.storage
      .from('listing_documents')
      .createSignedUrl(documentPath, 3600);
    if (signedUrlError || !signedUrlData?.signedUrl) {
      console.error('Failed to generate signed URL:', signedUrlError);
      toast.error('Document saved', {
        description: 'Could not analyze document for COA data.',
      });
      return 'failed';
    }
    const filename = documentPath.split('/').pop() || documentPath;
    return invokeProcessCOA({ ...args, signedUrl: signedUrlData.signedUrl, filename }, force);
  }, [invokeProcessCOA]);

  const getPendingConfirm = useCallback((listingId: string) =>
    pendingConfirms.find(p => p.listingId === listingId) ?? null, [pendingConfirms]);

  const confirmForce = useCallback(async (listingId?: string): Promise<COAOutcome> => {
    const current = listingId ? pendingConfirms.find(p => p.listingId === listingId) : pendingConfirms[0];
    if (!current) return 'failed';
    trackActivity('coa_force_processed', {
      listing_id: current.listingId,
      reason: current.reason,
    });
    // Pop the confirm before re-invoking so the dialog dismisses immediately.
    setPendingConfirms(prev => prev.filter(p => p !== current));
    return invokeProcessCOA(
      {
        listingId: current.listingId,
        documentPath: current.documentPath,
        companyId: current.companyId,
        listingStrain: current.listingStrain,
        signedUrl: current.coaUrl,
        filename: current.filename,
      },
      true,
    );
  }, [pendingConfirms, invokeProcessCOA]);

  const declineForce = useCallback((listingId?: string): void => {
    const current = listingId ? pendingConfirms.find(p => p.listingId === listingId) : pendingConfirms[0];
    if (!current) return;
    trackActivity('coa_force_declined', {
      listing_id: current.listingId,
      reason: current.reason,
    });
    setPendingConfirms(prev => prev.filter(p => p !== current));
  }, [pendingConfirms]);

  // Memoize the context value so consumers don't re-render on every
  // unrelated state tick inside the provider.
  const value = useMemo<COAProcessingContextValue>(() => ({
    startJob,
    startProcessing,
    getJobForListing,
    getResultForListing,
    isProcessing,
    dismissJob,
    getStatusMessage,
    bannerJob,
    dismissBanner,
    bannerSuggestionCount,
    pendingConfirm,
    confirmForce,
    declineForce,
    getPendingConfirm,
  }), [
    startJob, startProcessing, getJobForListing, getResultForListing, isProcessing,
    dismissJob, getStatusMessage, bannerJob, dismissBanner, bannerSuggestionCount,
    pendingConfirm, confirmForce, declineForce, getPendingConfirm,
  ]);

  return (
    <COAProcessingContext.Provider value={value}>
      {/* Render a poller for each active job */}
      {jobs.map(job => (
        <COAJobPoller
          key={job.jobId}
          job={job}
          onComplete={handleComplete}
          onTimeout={handleTimeout}
          onStatusUpdate={handleStatusUpdate}
        />
      ))}
      {children}
    </COAProcessingContext.Provider>
  );
}

export function useCOAProcessing() {
  const ctx = useContext(COAProcessingContext);
  if (!ctx) throw new Error('useCOAProcessing must be used within COAProcessingProvider');
  return ctx;
}
